import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { IController } from "../../../webgl_2d/core/types/controller_types.js";
import { Vfx2DManager } from "../../../webgl_2d/vfx_2d_manager.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";

export interface FeatureTutorialOverlayParams
{
  img_id: ImgID;
  rect: { x: number; y: number; width: number; height: number };
  timer?: {
    /**
     * Read live from the menu widget's own label: the countdown often is not
     * loaded yet at the moment the tutorial opens (the widget unlocks and the
     * overlay is raised on the same tick, before the feature's fetch resolves),
     * and a snapshot taken then would stay empty — and later stale — for the
     * whole time the modal is up.
     */
    get_text: () => string;
    x: number;
    y: number;
    width: number;
    height: number;
    daily_tasks_style: boolean;
  };
  on_confirm: () => void;
  dismissible?: boolean;
  on_dismiss?: () => void;
}

/**
 * One-time hint shown when a level-gated feature becomes available.
 * The overlay dims the whole screen (navigation included) via the standard
 * overlay background; on top of that it re-draws the feature widget with the
 * booster-style particle glow behind it and a pulsing cursor over it. A tap on
 * the widget closes the overlay and hands control back to the menu, which marks
 * the tutorial done and opens the feature. When dismissible, a tap anywhere else
 * closes it through on_dismiss without opening the feature.
 */
export class FeatureTutorialOverlay extends AbstractOverlay
{
  private static readonly CURSOR_PULSE_SCALE = 0.8;
  private static readonly CURSOR_PULSE_DURATION_MS = 650;

  private glow: IController | null = null;
  private confirmed = false;
  private readonly on_confirm: () => void;
  private readonly on_dismiss: ( () => void ) | undefined;
  private timer_label: UITextElement | null = null;
  private timer_get_text: ( () => string ) | null = null;
  private last_timer_text = "";

  public constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    params: FeatureTutorialOverlayParams,
    overlay_id: OverlayId
  )
  {
    super( ctx, x, y, width, height, overlay_id, params, params.dismissible === true );
    this.on_confirm = params.on_confirm;
    this.on_dismiss = params.on_dismiss;

    // params.rect arrives in canvas space (getAbsoluteRect includes the menu
    // root's safe-area origin), while children here are placed relative to
    // this overlay's root, which sits at the same safe-area origin — without
    // the conversion the insets are applied twice and the highlight drifts on
    // notched devices.
    const rect = {
      x: params.rect.x - x,
      y: params.rect.y - y,
      width: params.rect.width,
      height: params.rect.height,
    };

    const widget = new UIImage(
      this.ctx,
      rect.x,
      rect.y,
      rect.width,
      rect.height,
      params.img_id,
      {},
      true
    );
    widget.setOnClick( () =>
    {
      if ( this.confirmed )
        return;
      this.confirmed = true;
      if ( !this.getCloseOnBackgroundClick() )
        OverlayManager.closeOverlay();
    } );
    this.addChild( widget );

    const widget_abs = widget.getAbsolutePosition();
    this.glow = Vfx2DManager.addButtonBoosterGlowOnOverlay(
      new Vector2( widget_abs.x + rect.width / 2, widget_abs.y + rect.height / 2 ),
      new Vector2( rect.width, rect.height )
    );

    this.createTimer( rect, params.timer );
    this.createCursor( rect );
  }

  /** The timer belongs to the menu widget as a separate text child, not its bitmap. */
  private createTimer(
    widget_rect: { x: number; y: number; width: number; height: number },
    timer: FeatureTutorialOverlayParams[ "timer" ]
  ): void
  {
    if ( !timer ) return;

    this.timer_get_text = timer.get_text;
    this.last_timer_text = timer.get_text();

    const label = new UITextElement(
      this.ctx,
      widget_rect.x + timer.x,
      widget_rect.y + timer.y,
      timer.width,
      timer.height,
      timer.daily_tasks_style
        ? {
          text: this.last_timer_text,
          font_size: LayoutScaler.textSizeFromDesign( 29 ),
          min_font_size: LayoutScaler.textSizeFromDesign( 24 ),
          font_weight: 900,
          text_color: "#FFF3A6",
          text_style: TextStyleScheme.geologica,
          outline_width: LayoutScaler.textBorderSizeFromDesign( 5 ),
          outline_color: "#72159B",
          shadow_blur: LayoutScaler.widthFromDesign( 1 ),
          shadow_color: "#40005C",
          shadow_offset_y: LayoutScaler.yFromDesign( 2 ),
          auto_wrap: false,
          fit_font_size: true,
          scale_to_width: true,
        }
        : {
          text: this.last_timer_text,
          font_size: LayoutScaler.textSizeFromDesign( 30 ),
          font_weight: 900,
          text_style: TextStyleScheme.geologica,
          text_color: ColorScheme.adventure_widget_text,
          shadow_blur: LayoutScaler.widthFromDesign( 3 ),
          shadow_color: ColorScheme.black_transparent_80,
          shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
          auto_wrap: false,
          scale_to_width: true,
        }
    );
    label.setClickable( false );
    this.addChild( label, true );
    this.timer_label = label;
  }

  public override update( now: number ): void
  {
    super.update( now );

    if ( !this.timer_label || !this.timer_get_text )
      return;

    const text = this.timer_get_text();
    if ( text === this.last_timer_text )
      return;

    this.last_timer_text = text;
    this.timer_label.setText( text );
  }

  private createCursor( rect: { x: number; y: number; width: number; height: number } ): void
  {
    const cursor_w = LayoutScaler.widthFromDesign( 150 );
    const cursor_h = cursor_w * ( 183 / 159 );
    const cursor = new UIImage( this.ctx, 0, 0, cursor_w, cursor_h, ImgID.IconTutorialCursor );
    cursor.setClickable( false );

    const rest_pos = new Vector2(
      rect.x + rect.width / 2,
      rect.y + rect.height * 0.28
    );
    cursor.setPixelPos( rest_pos, true );
    this.addChild( cursor );

    const now = performance.now();
    const shrink = 1 - FeatureTutorialOverlay.CURSOR_PULSE_SCALE;
    const pressed_pos = new Vector2(
      rest_pos.x - shrink * cursor.getRect().getW2(),
      rest_pos.y - shrink * cursor.getRect().getH2()
    );
    const pulse_params = {
      easing: Easing.EaseInOutQuad,
      ping_pong_behaviour: true,
      infinite_loop: true,
    };

    cursor.addTransform( new TransformScale(
      cursor,
      true,
      null,
      new Vector2( FeatureTutorialOverlay.CURSOR_PULSE_SCALE, FeatureTutorialOverlay.CURSOR_PULSE_SCALE ),
      now,
      FeatureTutorialOverlay.CURSOR_PULSE_DURATION_MS,
      pulse_params
    ) );

    cursor.addTransform( new TransformStraightMove(
      cursor,
      true,
      rest_pos.clone(),
      pressed_pos,
      now,
      FeatureTutorialOverlay.CURSOR_PULSE_DURATION_MS,
      pulse_params
    ) );
  }

  public name(): string
  {
    return "FeatureTutorialOverlay";
  }

  public override onOverlayWillClose(): void
  {
    this.glow?.stop();
    this.glow = null;

    if ( this.confirmed )
      this.on_confirm();
    else
      this.on_dismiss?.();
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}
